import React, { useState } from "react";
import { Download, Edit3, X, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  {
    key: "monday",
    label: "Segunda · Gramática",
    accent: "#1e2a5a",
    defaults: {
      topic: "Passé composé vs. Imparfait",
      body:
        "Passé composé = ação pontual\n« Hier j'ai mangé une pizza. »\n\nImparfait = descrição, hábito\n« Quand j'étais petit, je jouais. »",
    },
  },
  {
    key: "tuesday",
    label: "Terça · Expressão",
    accent: "#d9381e",
    defaults: {
      topic: "« Avoir la gueule de bois »",
      body:
        "Literal: « ter a boca de madeira »\nReal: estar de ressaca. 🍷\n\nUse assim:\n« Ce matin j'ai la gueule de bois. »",
    },
  },
  {
    key: "wednesday",
    label: "Quarta · Vocabulário",
    accent: "#f4f1ea",
    defaults: {
      topic: "5 palavras: no café",
      body:
        "☕ un café serré  = curto\n🥐 un croissant   = croissant\n🍞 une baguette   = baguete\n💰 l'addition     = a conta\n🙋 s'il vous plaît= por favor",
    },
  },
  {
    key: "thursday",
    label: "Quinta · Cultura",
    accent: "#1e2a5a",
    defaults: {
      topic: "Por que os franceses se beijam?",
      body:
        "« La bise » = beijinho social\n\n2 em Paris, 4 em Provence.\nDe boca fechada — não é romântico.\n\nSó com quem você já conhece.",
    },
  },
  {
    key: "friday",
    label: "Sexta · Pronúncia",
    accent: "#1e2a5a",
    defaults: {
      topic: "O « U » francês",
      body:
        "Não é « iu » nem « u ».\nÉ como assobiar dizendo « i ».\n\nExercícios:\ntu · rue · lune · sur\n\nMétodo: lábios de « OU »\ncom língua de « I ».",
    },
  },
  {
    key: "saturday",
    label: "Sábado · Falso amigo",
    accent: "#1e2a5a",
    defaults: {
      topic: "« Actuellement » ≠ « Atualmente »",
      body:
        "« Actuellement » = agora mesmo\n« Actualmente » (PT) = HOJE EM DIA\n\nDica: pense « active »\nNão traduza direto.",
    },
  },
  {
    key: "sunday",
    label: "Domingo · Motivação",
    accent: "#f4f1ea",
    defaults: {
      topic: "Une langue,\né uma vida a mais.",
      body: "— Provérbio francês\n\n5 minutos por dia > 1h por semana.\n\nContinue.",
    },
  },
];

const CAPTIONS_PT = {
  monday:
    "🇫🇷 Segunda-gramática ! Passé composé vs. imparfait é o par que 9 em 10 brasileiros confundem. Salve esse post e revê depois da aula. Marca sua 1ª aula grátis no link da bio 👉 @francescomlionel #francesparabrasileiros",
  tuesday:
    "Expressão da semana ! Aprender francês é aprender a JOGAR com a língua. Se você entende essas expressões, você entende os franceses. Marca 1ª aula grátis 👉 link da bio #frances #francesparabrasileiros",
  wednesday:
    "Vocabulário essencial pra sobreviver num café parisiense sem pagar mico ☕ Salve pra próxima viagem. Quer aprender francês de verdade? Marca aula grátis no link da bio 👉 @francescomlionel",
  thursday:
    "Cultura francesa desmistificada. Os pequenos detalhes que fazem toda a diferença. Se curtir, comenta o que você quer saber sobre a França 👇 Marca 1ª aula grátis no link da bio 👉",
  friday:
    "Pronúncia é sobre MÚSCULO, não sobre inteligência. Esses 60s de treino por dia mudam TUDO em 3 semanas. Salva esse post. Quer feedback ao vivo? Marca aula grátis 👉 link da bio",
  saturday:
    "Falso amigo do dia! Essas armadilhas tiram nota em prova e te fazem passar vergonha em conversa real. Comenta um falso amigo que já te derrubou 👇 Aula grátis 👉 link da bio",
  sunday:
    "Domingo de motivação 🇫🇷 Aprender um idioma é escolher pertencer a outro mundo. Se você está começando ou retomando, comenta « JE COMMENCE » 👇 Marca sua 1ª aula grátis no link da bio 👉",
};

const AdminStories = () => {
  const [langAll, setLangAll] = useState("pt");
  const [editing, setEditing] = useState(null); // day key currently being edited
  const [overrides, setOverrides] = useState({}); // { day: {topic, body, lang} }
  const [nonce, setNonce] = useState(0); // force <img> reload on save
  const [copiedKey, setCopiedKey] = useState(null);

  const buildImgUrl = (day) => {
    const o = overrides[day.key] || {};
    const params = new URLSearchParams();
    params.set("lang", o.lang || langAll);
    if (o.topic) params.set("topic", o.topic);
    if (o.body) params.set("body", o.body);
    params.set("_", String(nonce));
    return `${API}/story/${day.key}?${params.toString()}`;
  };

  const openEditor = (day) => {
    const o = overrides[day.key] || {};
    setEditing({
      key: day.key,
      label: day.label,
      topic: o.topic ?? day.defaults.topic,
      body: o.body ?? day.defaults.body,
      lang: o.lang ?? langAll,
    });
  };

  const saveEditor = () => {
    if (!editing) return;
    setOverrides((prev) => ({
      ...prev,
      [editing.key]: {
        topic: editing.topic,
        body: editing.body,
        lang: editing.lang,
      },
    }));
    setNonce((n) => n + 1);
    setEditing(null);
    toast.success("Card atualizado.");
  };

  const resetEditor = () => {
    if (!editing) return;
    const d = DAYS.find((x) => x.key === editing.key);
    setEditing({
      ...editing,
      topic: d.defaults.topic,
      body: d.defaults.body,
    });
  };

  const copyCaption = async (day) => {
    try {
      await navigator.clipboard.writeText(CAPTIONS_PT[day.key] || "");
      setCopiedKey(day.key);
      toast.success("Caption copiada!");
      setTimeout(() => setCopiedKey(null), 2200);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  return (
    <div className="mt-8 space-y-6" data-testid="admin-stories-tab">
      <div className="bg-white border border-[#e5e3db] rounded-xl p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c]">
              Templates diários
            </p>
            <p className="mt-1 text-sm text-[#3a3a3a]">
              7 cards prontos (1080×1920, formato Instagram Story) — clique em{" "}
              <strong>Baixar</strong> ou <strong>Editar</strong> para
              customizar.
            </p>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <span className="uppercase tracking-[0.15em] text-[#5c5c5c]">
              Idioma padrão
            </span>
            <select
              value={langAll}
              onChange={(e) => {
                setLangAll(e.target.value);
                setNonce((n) => n + 1);
              }}
              data-testid="stories-lang-select"
              className="rounded-lg border border-[#e5e3db] px-3 py-1.5 text-sm bg-white"
            >
              <option value="pt">Português</option>
              <option value="en">English</option>
              <option value="fr">Français</option>
            </select>
          </label>
        </div>
      </div>

      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {DAYS.map((day) => {
          const src = buildImgUrl(day);
          const filename = `story-${day.key}-${(overrides[day.key]?.lang || langAll)}.png`;
          return (
            <div
              key={day.key}
              data-testid={`story-card-${day.key}`}
              className="bg-white border border-[#e5e3db] rounded-xl overflow-hidden flex flex-col"
            >
              <div className="relative bg-[#1e2a5a] aspect-[9/16]">
                <img
                  src={src}
                  alt={day.label}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  data-testid={`story-preview-${day.key}`}
                />
              </div>
              <div className="p-4 space-y-3">
                <p className="text-xs uppercase tracking-[0.15em] text-[#5c5c5c]">
                  {day.label}
                </p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={src}
                    download={filename}
                    data-testid={`story-download-${day.key}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#d9381e] px-3.5 py-1.5 text-xs font-semibold text-[#f4f1ea] hover:bg-[#1e2a5a] transition-colors"
                  >
                    <Download className="h-3.5 w-3.5" strokeWidth={2} />
                    Baixar
                  </a>
                  <button
                    type="button"
                    onClick={() => openEditor(day)}
                    data-testid={`story-edit-${day.key}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#1e2a5a] px-3.5 py-1.5 text-xs text-[#1e2a5a] hover:bg-[#1e2a5a] hover:text-[#f4f1ea] transition-colors"
                  >
                    <Edit3 className="h-3.5 w-3.5" strokeWidth={2} />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => copyCaption(day)}
                    data-testid={`story-caption-${day.key}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#e5e3db] px-3.5 py-1.5 text-xs text-[#5c5c5c] hover:border-[#1e2a5a] hover:text-[#1e2a5a] transition-colors"
                    title="Copiar caption pronta para o Instagram"
                  >
                    {copiedKey === day.key ? (
                      <Check className="h-3.5 w-3.5" strokeWidth={2} />
                    ) : (
                      <Copy className="h-3.5 w-3.5" strokeWidth={2} />
                    )}
                    Caption
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[#1e2a5a]/60 backdrop-blur-sm p-6"
          onClick={() => setEditing(null)}
          data-testid="story-edit-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <p className="font-display text-2xl text-[#1e2a5a]">
                {editing.label}
              </p>
              <button
                type="button"
                onClick={() => setEditing(null)}
                data-testid="story-edit-close"
                className="rounded-full p-1 text-[#5c5c5c] hover:bg-[#f0efea]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <label className="text-xs">
                <span className="uppercase tracking-[0.15em] text-[#5c5c5c]">
                  Idioma
                </span>
                <select
                  value={editing.lang}
                  onChange={(e) =>
                    setEditing((s) => ({ ...s, lang: e.target.value }))
                  }
                  data-testid="story-edit-lang"
                  className="mt-1 w-full rounded-lg border border-[#e5e3db] px-3 py-2 text-sm bg-white"
                >
                  <option value="pt">Português</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                </select>
              </label>
              <button
                type="button"
                onClick={resetEditor}
                className="self-end text-xs link-underline text-[#5c5c5c] mb-1"
              >
                Restaurar padrão
              </button>
            </div>

            <label className="mt-4 block">
              <span className="text-xs uppercase tracking-[0.15em] text-[#5c5c5c]">
                Título
              </span>
              <input
                type="text"
                value={editing.topic}
                onChange={(e) =>
                  setEditing((s) => ({ ...s, topic: e.target.value }))
                }
                data-testid="story-edit-topic"
                className="mt-1 w-full rounded-lg border border-[#e5e3db] px-3 py-2 text-sm"
              />
            </label>

            <label className="mt-4 block">
              <span className="text-xs uppercase tracking-[0.15em] text-[#5c5c5c]">
                Corpo (linhas com quebra respeitadas)
              </span>
              <textarea
                rows={8}
                value={editing.body}
                onChange={(e) =>
                  setEditing((s) => ({ ...s, body: e.target.value }))
                }
                data-testid="story-edit-body"
                className="mt-1 w-full rounded-lg border border-[#e5e3db] px-3 py-2 text-sm font-mono"
              />
            </label>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-full border border-[#e5e3db] px-5 py-2 text-sm text-[#5c5c5c] hover:border-[#1e2a5a] hover:text-[#1e2a5a] transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={saveEditor}
                data-testid="story-edit-save"
                className="inline-flex items-center gap-2 rounded-full bg-[#d9381e] px-5 py-2 text-sm font-semibold text-[#f4f1ea] hover:bg-[#1e2a5a] transition-colors"
              >
                Salvar e recarregar preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStories;
